import { MONTH_KEYS, dayNum, dueDay, localYmd } from '../../proposals/timeline-taches/dates';

// Date and name labels shared by the home page's blocks.

type TFn = (key: string, params?: Record<string, string | number>) => string;

const DAY_KEYS = ['home.day.sun', 'home.day.mon', 'home.day.tue', 'home.day.wed', 'home.day.thu', 'home.day.fri', 'home.day.sat'];

const pad = (n: number) => String(n).padStart(2, '0');
const clock = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const todayNum = () => dayNum(localYmd(new Date()));

/** "4 nov." */
export function dayMonth(d: Date, t: TFn): string {
  return `${d.getDate()} ${t(MONTH_KEYS[d.getMonth()])}`;
}

/** A due date ('YYYY-MM-DD…') as "4 nov." */
export function dueLabel(value: string, t: TFn): string {
  const [y, m, d] = dueDay(value).split('-').map(Number);
  return dayMonth(new Date(y, m - 1, d), t);
}

/** Whether a due date ('YYYY-MM-DD…') is before today. */
export const isOverdue = (value: string | null) => !!value && dayNum(dueDay(value)) < todayNum();

/** A meeting: "Jeu 12 nov. · 10:00" (no time for an all-day one). */
export function meetingLabel(iso: string, allDay: boolean, t: TFn): string {
  const d = new Date(iso);
  return `${t(DAY_KEYS[d.getDay()])} ${dayMonth(d, t)}${allDay ? '' : ` · ${clock(d)}`}`;
}

/** When something happened: "aujourd'hui, 14:05", "hier, 16:40", "Lun 26 oct.". */
export function whenLabel(iso: string, t: TFn): string {
  const d = new Date(iso);
  const diff = todayNum() - dayNum(localYmd(d));
  if (diff <= 0) return t('home.when.today', { time: clock(d) });
  if (diff === 1) return t('home.when.yesterday', { time: clock(d) });
  if (diff < 7) return `${t(DAY_KEYS[d.getDay()])} ${dayMonth(d, t)}`;
  return dayMonth(d, t);
}

/** "CR" for "Camille Rochat". */
export function initials(name: string): string {
  return name.split(/[\s-]+/).filter(Boolean).map(w => w[0]).join('').slice(0, 2).toUpperCase();
}

/** A task column label that means work is under way. */
export const isInProgressLabel = (label: string) => /in progress|en cours/i.test(label);
export const isBlockedLabel = (label: string) => /bloqu|blocked/i.test(label);

/** The class a task status pill takes, from its column label. */
export function statusClass(label: string): string {
  if (isBlockedLabel(label)) return 'is-blocked';
  if (isInProgressLabel(label)) return 'is-doing';
  if (/backlog/i.test(label)) return 'is-backlog';
  if (/done|termin|fait/i.test(label)) return 'is-done';
  return 'is-todo';
}
